import React, { useCallback, useRef, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useProductsInfiniteScroll } from '../hooks/useProductsInfiniteScroll';
import './ProductsList.css';
import rollDinamitaImage from '../assets/Tipos de Rolls/ROLL Camaron - Dinamita.png';

const AVAILABILITY_ALL = '';
const AVAILABILITY_AVAILABLE = 'available';
const AVAILABILITY_UNAVAILABLE = 'unavailable';
const SORT_PRICE_ASC = 'price_asc';
const SORT_PRICE_DESC = 'price_desc';
const SORT_NAME_ASC = 'name_asc';
const SORT_NAME_DESC = 'name_desc';

/** Fallback product list for categories without API mapping. */
const STATIC_PRODUCTS = [
  { id: 1, name: 'Fede Roll', price: '16.00', description: 'Marumi Exclusive', image: rollDinamitaImage },
  { id: 2, name: 'Marumi Roll', price: '18.00', description: 'Marumi Exclusive', image: rollDinamitaImage },
  { id: 3, name: 'Butter Crab', price: '17.00', description: '', image: rollDinamitaImage },
  { id: 4, name: 'Isa Roll', price: '15.00', description: 'Marumi Exclusive', image: rollDinamitaImage },
  { id: 5, name: 'Alaska Roll', price: '13.00', description: '', image: rollDinamitaImage },
  { id: 6, name: 'Fuji Roll', price: '14.00', description: 'Marumi Exclusive', image: rollDinamitaImage },
];

/** Visible category names (Spanish). */
const CATEGORY_LABELS = {
  todos: 'Todos',
  'rolls-mixtos': 'Sushi',
  'pollo-crispy': 'Pollo crispy',
};

/** API options per category slug: category_id and per_page for infinite scroll. */
const CATEGORY_API_OPTIONS = {
  todos: {},
  'rolls-mixtos': { categoryId: 12, perPage: 15 },
  'pollo-crispy': { categoryId: 13, perPage: 15 },
};

function ProductsList() {
  const { category } = useParams();
  const navigate = useNavigate();
  const scrollOptions = CATEGORY_API_OPTIONS[category] ?? {};
  const isApiCategory = category in CATEGORY_API_OPTIONS;

  const {
    products: apiProducts,
    loading,
    loadingMore,
    error,
    hasMore,
    total,
    loadMore,
  } = useProductsInfiniteScroll(scrollOptions);

  const [availabilityFilter, setAvailabilityFilter] = React.useState(AVAILABILITY_ALL);
  const [priceFilter, setPriceFilter] = React.useState('');
  const [sortBy, setSortBy] = React.useState('');
  const sentinelRef = useRef(null);

  const filteredAndSortedProducts = useMemo(() => {
    let list = [...apiProducts];
    if (availabilityFilter === AVAILABILITY_AVAILABLE) list = list.filter((p) => p.is_active);
    if (availabilityFilter === AVAILABILITY_UNAVAILABLE) list = list.filter((p) => !p.is_active);
    const order = sortBy || (priceFilter === 'asc' ? SORT_PRICE_ASC : priceFilter === 'desc' ? SORT_PRICE_DESC : '');
    if (order === SORT_PRICE_ASC) list.sort((a, b) => (a.price_eur ?? (parseFloat(a.price) || 0)) - (b.price_eur ?? (parseFloat(b.price) || 0)));
    if (order === SORT_PRICE_DESC) list.sort((a, b) => (b.price_eur ?? (parseFloat(b.price) || 0)) - (a.price_eur ?? (parseFloat(a.price) || 0)));
    if (order === SORT_NAME_ASC) list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
    if (order === SORT_NAME_DESC) list.sort((a, b) => (b.name || '').localeCompare(a.name || '', 'es'));
    return list;
  }, [apiProducts, availabilityFilter, priceFilter, sortBy]);

  const handleProductClick = useCallback(
    (productId) => {
      navigate(`/producto/${productId}`);
    },
    [navigate]
  );

  useEffect(() => {
    if (!isApiCategory || !hasMore || loading || loadingMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) loadMore();
      },
      { rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isApiCategory, hasMore, loading, loadingMore, loadMore]);

  const products = isApiCategory ? filteredAndSortedProducts : STATIC_PRODUCTS;
  const categoryName = CATEGORY_LABELS[category] || 'Productos';

  return (
    <section className="products-list">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Inicio</Link>
          <span> / </span>
          <Link to="/categorias">CATEGORÍAS</Link>
          <span> / </span>
          <span>{categoryName}</span>
        </div>

        <div className="products-header">
          <div className="products-filters">
            <div className="filter-group">
              <label>Filtrar</label>
              <select
                className="filter-select"
                value={availabilityFilter}
                onChange={(e) => setAvailabilityFilter(e.target.value)}
                aria-label="Filter by availability"
              >
                <option value={AVAILABILITY_ALL}>Disponibilidad</option>
                <option value={AVAILABILITY_AVAILABLE}>Disponible</option>
                <option value={AVAILABILITY_UNAVAILABLE}>No disponible</option>
              </select>
              <select
                className="filter-select"
                value={priceFilter}
                onChange={(e) => setPriceFilter(e.target.value)}
                aria-label="Filter by price"
              >
                <option value="">Precio</option>
                <option value="asc">Menor a mayor</option>
                <option value="desc">Mayor a menor</option>
              </select>
            </div>
            <div className="products-count">
              <span>
                {`${products.length} productos`}
              </span>
            </div>
          </div>
          <div className="products-sort">
            <label>Ordenar por</label>
            <select
              className="sort-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              aria-label="Sort products"
            >
              <option value="">Seleccionar</option>
              <option value={SORT_PRICE_ASC}>Precio: menor a mayor</option>
              <option value={SORT_PRICE_DESC}>Precio: mayor a menor</option>
              <option value={SORT_NAME_ASC}>Nombre A-Z</option>
              <option value={SORT_NAME_DESC}>Nombre Z-A</option>
            </select>
          </div>
        </div>

        {isApiCategory && error && (
          <p className="products-list-error" role="alert">
            No se pudieron cargar los productos.
          </p>
        )}

        {isApiCategory && loading && products.length === 0 ? (
          <div className="products-list-loading" aria-busy="true">
            <p>Cargando productos…</p>
          </div>
        ) : (
          <>
            <div className="products-grid">
              {products.map((product) => (
                <article
                  key={product.id}
                  className="product-card"
                  onClick={() => handleProductClick(product.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleProductClick(product.id);
                    }
                  }}
                  aria-label={`View ${product.name}`}
                >
                  {product.isNew && (
                    <div className="product-badge" aria-hidden>NUEVO</div>
                  )}
                  <div className="product-image-container">
                    <img
                      src={product.image || rollDinamitaImage}
                      alt={product.name}
                      className="product-image"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = rollDinamitaImage;
                      }}
                    />
                  </div>
                  <div className="product-content">
                    <span className="product-ref">Ref {product.price}</span>
                    <h3 className="product-name">{product.name}</h3>
                    {product.description && (
                      <p className="product-description">{product.description}</p>
                    )}
                    <button
                      type="button"
                      className="add-to-cart-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        // TODO: add to cart
                      }}
                      aria-label={`Add ${product.name} to cart`}
                    >
                      Añadir al carrito
                    </button>
                  </div>
                </article>
              ))}
            </div>

            {isApiCategory && (
              <>
                <div
                  ref={sentinelRef}
                  className="products-infinite-sentinel"
                  aria-hidden
                />
                {loadingMore && (
                  <div className="products-list-loading-more" aria-busy="true">
                    <span className="products-list-spinner" />
                    <p>Cargando más…</p>
                  </div>
                )}
                {!loading && !loadingMore && products.length > 0 && !hasMore && (
                  <p className="products-list-end">Se cargaron todos los productos.</p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default ProductsList;
